/**
 * M24 task-09 — pure spec-edit helpers for the DB-connector schema editor
 * (DBC-04; HUB-079 §4/§6). Kept dependency-light (no hub-client import) so
 * the no-jsdom unit tests can pin them directly.
 *
 * The checkbox convention: `active` ABSENT means active — only an explicit
 * `active: false` unchecks (the shared spec schema, DBC-04). Toggling always
 * returns a NEW spec object; inputs are never mutated (the server validates
 * on every save, so the caller can refetch-revert on 422).
 */
import type {
  DbConnectorSpec,
  DbConnectorSpecModel,
} from '@verybigthings/trackframe-shared/schemas';

export type SpecMemberKind = 'dimension' | 'metric' | 'hierarchy';

/** Checked = `active !== false` (absent means active). */
export function isSpecActive(entry: { active?: boolean }): boolean {
  return entry.active !== false;
}

function toggleMember<T extends { name: string; active?: boolean }>(
  members: T[],
  memberName: string,
): T[] {
  return members.map((m) => (m.name === memberName ? { ...m, active: !isSpecActive(m) } : m));
}

function toggleInModel(
  model: DbConnectorSpecModel,
  memberKind?: SpecMemberKind,
  memberName?: string,
): DbConnectorSpecModel {
  if (!memberKind || memberName === undefined) {
    return { ...model, active: !isSpecActive(model) };
  }
  switch (memberKind) {
    case 'dimension':
      return { ...model, dimensions: toggleMember(model.dimensions, memberName) };
    case 'metric':
      return { ...model, metrics: toggleMember(model.metrics, memberName) };
    case 'hierarchy':
      return {
        ...model,
        categoricalHierarchies: toggleMember(model.categoricalHierarchies ?? [], memberName),
      };
  }
}

/**
 * Flip the `active` flag on a model (no member args) or on one of its
 * dimensions/metrics/hierarchies. Returns a NEW spec; never mutates `spec`.
 */
export function toggleActive(
  spec: DbConnectorSpec,
  modelName: string,
  memberKind?: SpecMemberKind,
  memberName?: string,
): DbConnectorSpec {
  return {
    ...spec,
    models: spec.models.map((model) =>
      model.name === modelName ? toggleInModel(model, memberKind, memberName) : model,
    ),
  };
}
